import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const styles = {
  tableHeader: {
    padding: "10px",
    textAlign: "left",
    borderBottom: "1px solid #E0E0E0",
  },
  tableData: {
    padding: "10px",
    borderBottom: "1px solid #E0E0E0",
  },
  nameColumn: {
    width: "60%", 
  },
  typeColumn: {
    width: "20%", 
  },
  thumbnailColumn: {
    width: "10%", 
  },
  actionColumn: {
    width: "10%", 
    textAlign: "center", 
  },
  paginationButton: {
    padding: "5px 10px",
    backgroundColor: "white",
    border: "1px solid #E0E0E0",
    borderRadius: "5px",
    cursor: "pointer",
  },
  expandedRow: {
    backgroundColor: "#F9F9F9",
  },
  tab: {
    padding: "10px 20px",
    cursor: "pointer",
    borderBottom: "2px solid transparent",
  },
  activeTab: {
    borderBottom: "2px solid black",
    fontWeight: "bold",
  },
};

const getTypeLabelAndIcon = (type) => {
  switch (type) {
    case "HTML":
      return { label: "WebPage", icon: "web.svg" }; // Replace with actual URL or path to image
    case "VIDEO":
      return { label: "Video", icon: "video.svg" }; // Replace with actual URL or path to image
    case "PDF":
      return { label: "PDF", icon: "pdf.svg" }; // Replace with actual URL or path to image
    case "DOCX":
      return { label: "Word", icon: "word.svg" }; // Replace with actual URL or path to image
    case "PPT":
      return { label: "PPT", icon: "ppt.svg" }; // Replace with actual URL or path to image
    case "IMAGE":
      return { label: "Image", icon: "image.svg" }; // Replace with actual URL or path to image
    default:
      return { label: "File", icon: "file.svg" }; // Replace with actual URL or path to image
  }
};


function formatCourseMaterials(input, keywords, searchMode) {
  const formattedData = [];

  if (searchMode === "OR" || keywords.length === 1) {
    for (const courseCode in input.Search_Courses_Results_Dict) {
      const course = input.Search_Courses_Results_Dict[courseCode];
      for (const section in course) {
        const materials = course[section].Matched_Material_Info_Dict;
        if (materials) {
          for (const materialId in materials) {
            const material = materials[materialId];
            const urls = material.Matched_Paragraph_Unique_URL_List;

            // Map each URL to matched keywords
            const urlKeywordsMap = urls.map((url) => {
              const matchedKeywords = keywords.filter((keyword) => url.includes(keyword) || section.includes(keyword));
              return {
                URL: url,
                Matched_Keywords: matchedKeywords,
              };
            });

            // Add to formattedData if any keywords are matched
            if (urlKeywordsMap.some((item) => item.Matched_Keywords.length > 0)) {
              formattedData.push({
                Course: courseCode,
                Section: section,
                MaterialID: materialId,
                Material: material.Matched_Material_Info,
                URLKeywordsMap: urlKeywordsMap,
              });
            }
          }
        }
      }
    }
  } else if (searchMode === "ALL") {
    for (const courseCode in input.Search_Courses_Logic_Results_Dict) {
      const logicCourse = input.Search_Courses_Logic_Results_Dict[courseCode];
      const logicMaterials = logicCourse.Logic_Matched_Material_Info_Dict;
  
      // Iterate over each material ID in Logic_Matched_Material_Info_Dict
      for (const materialId in logicMaterials) {
        const logicMaterial = logicMaterials[materialId];
  
        // Navigate back to Search_Courses_Results_Dict for the same course and material ID
        const courseSections = input.Search_Courses_Results_Dict[courseCode];
        if (!courseSections) continue;
  
        const sectionUrlsMap = {};
        const urlKeywordsMap = [];
        let keywordTracker = new Set();
  
        // Iterate over each section within the course
        for (const section in courseSections) {
          const materialsDict = courseSections[section].Matched_Material_Info_Dict;
  
          // Check if the material ID exists in this section
          if (materialsDict && materialsDict[materialId]) {
            const materialInfo = materialsDict[materialId];
            const urls = materialInfo.Matched_Paragraph_Unique_URL_List;
  
            // Ensure the section is initialized in the map
            if (!sectionUrlsMap[section]) {
              sectionUrlsMap[section] = [];
            }
  
            // Map each URL with matched keywords and track keyword appearances
            urls.forEach((url) => {
              const matchedKeywords = keywords.filter((keyword) => url.includes(keyword) || section.includes(keyword));
              
              // Track each matched keyword across this materialId
              matchedKeywords.forEach((kw) => keywordTracker.add(kw));
  
              // Add to sectionUrlsMap and URLKeywordsMap
              sectionUrlsMap[section].push({ URL: url, Matched_Keywords: matchedKeywords });
              urlKeywordsMap.push({ URL: url, Matched_Keywords: matchedKeywords });
            });
          }
        }
  
        // Check if all keywords are present in any of the URLs for this materialId
        const allKeywordsMatched = keywords.every((keyword) => keywordTracker.has(keyword));
  
        // Only add to formattedData if all keywords have been matched at least once
        if (allKeywordsMatched) {
          formattedData.push({
            Course: courseCode,
            MaterialID: materialId,
            Material: logicMaterial.Logic_Matched_Material_Info,
            SectionUrlsMap: sectionUrlsMap,
            URLKeywordsMap: urlKeywordsMap,
          });
        }
      }
    }
  }

  // console.log("formattedData", formattedData);

  return formattedData;
}


const SearchResults = () => {
  const [result, setResult] = useState(null);
  const [typeCounts, setTypeCounts] = useState({});
  const [logicCounts, setLogicCounts] = useState({});
  const [downloadUrl, setDownloadUrl] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const { keywords, courses, searchMode } = location.state || { keywords: [], courses: [], searchMode };

  const [allResults, setAllResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [expanded, setExpanded] = useState({});
  const [selectedCourse, setSelectedCourse] = useState(courses[0] || "");

  const itemsPerPage = 6;

  useEffect(() => {
    const fetchResults = async () => {
      setLoading(true); // Set loading to true when starting a new request

      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");

      

      let raw = JSON.stringify({
        "Course_Code_List_Input": courses,
        "Search_Keywords_List_Input": keywords,
        "Logic_Type_Input": searchMode,
      });

      if (searchMode === "ALL") {
        raw = JSON.stringify({
          "Course_Code_List_Input": courses,
          "Search_Keywords_List_Input": keywords,
          "Logic_Type_Input": "All",
        });
      }

      if (searchMode === "OR") {
        raw = JSON.stringify({
          "Course_Code_List_Input": courses,
          "Search_Keywords_List_Input": keywords,
          "Logic_Type_Input": "Any",
        });
      }

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: raw,
        redirect: "follow",
      };

      try {
        const response = await fetch("https://be2.aristai.io/api/search/keywords", requestOptions);
        const result = await response.json();
        

        // console.log(result.Search_Courses_Logic_Results_Dict, "search results");

        const formattedOutput = formatCourseMaterials(result, keywords, searchMode);

        setTypeCounts(countTimes(result.Search_Courses_Results_Dict[selectedCourse], keywords));
        setLogicCounts(countLogicTimes(result.Search_Courses_Logic_Results_Dict[selectedCourse], keywords));
        setDownloadUrl(result.download_url);

        setResult(result);

      


        setAllResults(formattedOutput);
      } catch (error) {
        console.error("Error fetching search results:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [courses, keywords]);

  const handlePageChange = (page) => {
    setExpanded({});
    setCurrentPage(page);
  };

  useEffect(() => {
      if (result) {
        setTypeCounts(countTimes(result.Search_Courses_Results_Dict[selectedCourse], keywords));
        setLogicCounts(countLogicTimes(result.Search_Courses_Logic_Results_Dict[selectedCourse], keywords));
      }
      
  }, [selectedCourse]);

  const toggleExpand = (index) => {
    setExpanded((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const countTimes = (result, keywords) => {
      console.log("result", result);
      let keywordCounts = {};
      const currentRes = result;


      console.log("keywords", keywords);

      for (const keyword of keywords) {
        console.log("keyword", keyword);

        // Initialize the object if it doesn't exist
        if (!keywordCounts[keyword]) {
            keywordCounts[keyword] = {};
        }
        // Assign values from currentRes
        keywordCounts[keyword]["total_para"] = currentRes[keyword]?.Total_Number_of_Matched_Paragraph || 0;
        keywordCounts[keyword]["total"] = currentRes[keyword]?.Total_Number_of_Matched_Material || 0;
        keywordCounts[keyword]["video"] = currentRes[keyword]?.Total_Number_of_Matched_Video || 0;
        keywordCounts[keyword]["html"] = currentRes[keyword]?.Total_Number_of_Matched_HTML || 0;
        keywordCounts[keyword]["document"] = currentRes[keyword]?.Total_Number_of_Matched_Document || 0;
      }

      // console.log("keywordCounts", keywordCounts);
      return keywordCounts;
    
  };


  const countLogicTimes = (result) => {
      let totalCount = result.Total_Number_of_Logic_Matched_Material || 0;
      let videoCount = result.Total_Number_of_Logic_Matched_Video || 0;
      let htmlCount = result.Total_Number_of_Logic_Matched_HTML || 0;
      let documentCount = result.Total_Number_of_Logic_Matched_Document || 0;
      return {totalCount, videoCount, htmlCount, documentCount };
  };
  

  // Filter results for the selected course
  const filteredResults = allResults.filter((item) => item.Course === selectedCourse);

  

  // Pagination calculation
  const totalItems = filteredResults.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedResults = filteredResults.slice(startIndex, startIndex + itemsPerPage);



  return (
    <div className="search-main" style={{ width: "1000px", margin: "0 auto", padding: "20px", border: "1px solid #E0E0E0", borderRadius: "8px" }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2>Search Results</h2>
        <button
          style={{
            padding: "10px 20px",
            backgroundColor: "white",
            color: "black",
            borderRadius: "5px",
            border: "1px solid #5C5C5C",
            cursor: "pointer",
            marginLeft: "350px",
          }}
          onClick={() => {
            const url = downloadUrl; // Replace with your actual URL
            const fileName = "Search Results.xlsx"; // Desired file name
          
            // Fetch the file as a Blob
            fetch(url)
              .then((response) => {
                if (!response.ok) {
                  throw new Error("Network response was not ok");
                }
                return response.blob(); // Convert response to Blob
              })
              .then((blob) => {
                // Create an object URL for the Blob
                const blobUrl = URL.createObjectURL(blob);
          
                // Create an invisible anchor element
                const anchor = document.createElement("a");
                anchor.href = blobUrl;
                anchor.download = fileName; // Set the desired file name
          
                // Append anchor to the document, click it, and remove it
                document.body.appendChild(anchor);
                anchor.click();
                document.body.removeChild(anchor);
          
                // Revoke the object URL to free memory
                URL.revokeObjectURL(blobUrl);
              })
              .catch((error) => {
                console.error("Error downloading the file:", error);
              });
          }}
          
        >
          Download Results
        </button>
        <button onClick={() => navigate(-1)} style={{ padding: "10px 20px", backgroundColor: "white", color: "black", borderRadius: "5px", border: "1px solid #5C5C5C", cursor: "pointer" }}>
          Go back
        </button>
      </header>

      {/* Course Tabs */}
      <div className="course-tabs" style={{ display: "flex", gap: "20px", margin: "20px 0", borderBottom: "1px solid #E0E0E0" }}>
        {courses.map((course) => (
          <div
            key={course}
            onClick={() => {
              setSelectedCourse(course);
              setCurrentPage(1); // Reset page number when switching courses
            }}
            style={{
              ...styles.tab,
              ...(selectedCourse === course ? styles.activeTab : {}),
            }}
          >
            {course}
          </div>
        ))}
      </div>

      <div style={{ marginBottom: "20px" }}>
    
    {(searchMode === "ALL" && keywords.length > 1) ? (
         
          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              <div
                  style={{
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      padding: "15px",
                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                      backgroundColor: "#fff",
                      marginBottom: "20px",
                  }}
              >
                  <div
                      style={{
                          marginBottom: "15px",
                          fontWeight: "bold",
                          fontSize: "1.1rem",
                          color: "#333",
                          marginTop: "10px",
                      }}
                  >
                      Total Matches <span style={{ float: "right", color: "#777" }}>{logicCounts?.totalCount || 0} in total</span>
                  </div>
                  <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                      <span
                          style={{
                              border: "1px solid #ddd",
                              padding: "5px 10px",
                              borderRadius: "5px",
                              backgroundColor: "#f9f9f9",
                          }}
                      >
                          Webpages x {logicCounts?.htmlCount || 0}
                      </span>
                      <span
                          style={{
                              border: "1px solid #ddd",
                              padding: "5px 10px",
                              borderRadius: "5px",
                              backgroundColor: "#f9f9f9",
                          }}
                      >
                          Videos x {logicCounts?.videoCount || 0}
                      </span>
                      <span
                          style={{
                              border: "1px solid #ddd",
                              padding: "5px 10px",
                              borderRadius: "5px",
                              backgroundColor: "#f9f9f9",
                          }}
                      >
                          Documents x {logicCounts?.documentCount || 0}
                      </span>
                  </div>
              </div>
          </div>

    ) : (
      <></>
    )}

<div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      {keywords.map((keyword, index) => (
        <div
          key={index}
          style={{
            border: "1px solid #ddd",
            borderRadius: "8px",
            padding: "15px",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            backgroundColor: "#fff",
          }}
        >
          <div
            style={{
              marginBottom: "15px",
              fontWeight: "bold",
              fontSize: "1.1rem",
              color: "#333",
              marginTop: "10px",
            }}
          >
            {keyword} <span style={{ float: "right", color: "#777" }}>{typeCounts[keyword]?.total_para || 0} paragraphs in {typeCounts[keyword]?.total || 0} materials</span>
          </div>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <span
              style={{
                border: "1px solid #ddd",
                padding: "5px 10px",
                borderRadius: "5px",
                backgroundColor: "#f9f9f9",
              }}
            >
              Webpages x {typeCounts[keyword]?.html || 0}
            </span>
            <span
              style={{
                border: "1px solid #ddd",
                padding: "5px 10px",
                borderRadius: "5px",
                backgroundColor: "#f9f9f9",
              }}
            >
              Videos x {typeCounts[keyword]?.video || 0}
            </span>
            <span
              style={{
                border: "1px solid #ddd",
                padding: "5px 10px",
                borderRadius: "5px",
                backgroundColor: "#f9f9f9",
              }}
            >
              Documents x {typeCounts[keyword]?.document || 0}
            </span>
          </div>
        </div>
      ))}
    </div>
</div>


      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "20px" }}>
            <thead>
              <tr>
                <th style={{ ...styles.tableHeader, ...styles.nameColumn }}>Name</th>
                <th style={{ ...styles.tableHeader, ...styles.typeColumn }}>Content Type</th>
                <th style={{ ...styles.tableHeader, ...styles.thumbnailColumn }}>Thumbnail</th>
                <th style={{ ...styles.tableHeader, ...styles.actionColumn }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedResults.map((item, index) => (
                <React.Fragment key={startIndex + index}>
                  <tr>
                    <td style={{ ...styles.tableData, ...styles.nameColumn }}>
                      {item.Material.Title ? (
                        <a href={item.Material["Video URL"]} target="_blank" rel="noopener noreferrer">
                          {item.Material.Title}
                        </a>
                      ) : null}
                    </td>
                    <td style={{ ...styles.tableData, ...styles.typeColumn }}>
                      {item.Material["Material Type"] ? (
                        (() => {
                          const { label, icon } = getTypeLabelAndIcon(item.Material["Material Type"]);
                          return (
                            <>
                              <img src={icon} alt={label} style={{ width: "20px", height: "20px", marginRight: "5px", verticalAlign: "middle"  }} />
                              <span>{label}</span>
                            </>
                          );
                        })()
                      ) : null}
                    </td>
                    <td className="thumbnail" style={{ ...styles.tableData, ...styles.thumbnailColumn }}>
                      {item.Material["Thumbnail URL"] ? (
                        <a href={item.Material["Thumbnail URL"]} target="_blank" rel="noopener noreferrer">
                          <img
                            className="thumbnail-img"
                            src={item.Material["Thumbnail URL"]}
                            alt={`${item.Material["Title"]} Thumbnail`}
                          />
                        </a>
                      ) : null}
                    </td>
                    <td className="show-icon" style={{ ...styles.tableData, ...styles.actionColumn }}>
                      <button
                        onClick={() => toggleExpand(startIndex + index)}
                        className="action-button"
                      >
                        <img
                          className="show-img"
                          src={expanded[startIndex + index] ? "hide.svg" : "show.svg"}
                          alt={expanded[startIndex + index] ? "Hide" : "Show"}
                        />
                      </button>
                    </td>
                  </tr>
                  {expanded[startIndex + index] && item.URLKeywordsMap?.some((urlItem) => urlItem.Matched_Keywords.length >= 0) && (
                    <tr>
                      <td colSpan="4" className={`expanded-row ${expanded[startIndex + index] ? 'expanded' : ''}`} style={{ ...styles.expandedRow }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                          <ul>
                            {item.URLKeywordsMap.filter(urlItem => urlItem.Matched_Keywords.length > 0).map((urlItem, idx) => {
                              let displayText;
                              if (item.Material["Material Type"] === "HTML") {
                                displayText = item.Material.Title;
                              } else if (item.Material["Material Type"] === "PDF") {
                                const pageMatch = urlItem.URL.match(/#page=(\d+)/);
                                displayText = pageMatch ? `Page ${pageMatch[1]}` : "Page unknown";
                              } else if (item.Material["Material Type"] === "VIDEO") {
                                const timeMatch = urlItem.URL.match(/st=(\d+)/);
                                if (timeMatch) {
                                  const totalSeconds = parseInt(timeMatch[1]);
                                  const hours = Math.floor(totalSeconds / 3600);
                                  const minutes = Math.floor((totalSeconds % 3600) / 60);
                                  const seconds = totalSeconds % 60;
                                  displayText = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
                                } else {
                                  displayText = "00:00:00";
                                }
                              } else {
                                displayText = item.Material.Title;
                              }

                              return (
                                <li key={idx} style={{ display: "flex", alignItems: "center", gap: "20px", paddingTop: "10px" }}>
                                  <a className="expand-child" href={urlItem.URL} target="_blank" rel="noopener noreferrer" style={{ color: "#3B82F6", display: "flex", alignItems: "center", justifyContent: "start", width: "400px" }}>
                                    <span style={{ fontWeight: "bold", color: "#000000", marginRight: "5px" }}>{displayText}</span>
                                    <img className="hyper" src="hyper.svg" alt="External Link" />
                                  </a>
                                  <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", marginLeft: "10px" }}>
                                    {urlItem.Matched_Keywords.map((keyword, index) => (
                                      <span key={index} style={{
                                        padding: "10px 15px",
                                        backgroundColor: "#ffffff",
                                        borderRadius: "5px",
                                        fontSize: "16px",
                                        color: "#000000",
                                        boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
                                        border: "1px solid #000000",
                                      }}>
                                        {keyword}
                                      </span>
                                    ))}
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          <footer style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "20px" }}>
            <p>
              Showing {(startIndex + 1)} to {Math.min(startIndex + itemsPerPage, totalItems)} of {totalItems} entries
            </p>
            <div style={{ display: "flex", gap: "5px" }}>
              <button
                className="pgn-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  backgroundColor: currentPage === 1 ? "#f1f1f1" : "initial",
                  color: currentPage === 1 ? "#c1c1c1" : "initial",
                  opacity: currentPage === 1 ? 0.6 : 1,
                  border: "1px solid #E0E0E0",
                  borderRadius: "5px",
                }}
              >
                <img src="Left.svg" alt="left icon" />
              </button>
              {[...Array(totalPages).keys()].map((p) => (
                <button
                  key={p + 1}
                  onClick={() => handlePageChange(p + 1)}
                  style={{
                    ...styles.paginationButton,
                    ...(currentPage === p + 1
                      ? { backgroundColor: "black", color: "white" } // Active style for the current page
                      : {}),
                  }}
                >
                  {p + 1}
                </button>
              ))}
              <button
                className="pgn-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{
                  cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                  backgroundColor: currentPage === totalPages ? "#f1f1f1" : "initial",
                  color: currentPage === totalPages ? "#c1c1c1" : "initial",
                  opacity: currentPage === totalPages ? 0.6 : 1,
                  border: "1px solid #E0E0E0",
                  borderRadius: "5px",
                }}
              >
                <img src="Right.svg" alt="right icon" />
              </button>
            </div>
          </footer>
        </>
      )}
    </div>
  );
};

export default SearchResults;
